/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';
import {
  Modal,
  Platform,
  StyleSheet,
  TouchableHighlight,
  View,
} from 'react-native';
import DyteClient from '@dytesdk/web-core';
import { useDyteSelector } from '@dytesdk/react-native-core';
import { SvgXml } from 'react-native-svg';
import { SidebarSection, States } from '../types/props';
import { defaultIconPack, IconPack } from '../lib/icons';
import { DyteI18n, useLanguage } from '../lib/lang';
import { UIConfig } from '../types/ui-config';
import { defaultConfig } from '../lib/default-ui-config';
import DyteChat from './DyteChat';
import DyteText from './DyteText';
import DyteParticipants from './DyteParticipants';
import DytePolls from './DytePolls';
import DytePlugins from './DytePlugins';
import {
  canViewChat,
  canViewParticipants,
  canViewPlugins,
  canViewPolls,
} from '../utils/permissions';
import { DyteUIContext } from '../lib/context-provider/ui-context';

export default function ({
  meeting,
  config = defaultConfig,
  iconPack = defaultIconPack,
  size = 'sm',
  states,
  defaultSection = 'chat',
  enabledSections = ['chat', 'polls', 'participants', 'plugins'],
  t,
}: {
  meeting: DyteClient;
  config?: UIConfig;
  iconPack?: IconPack;
  size?: 'lg' | 'md' | 'sm' | 'xl';
  states?: States;
  defaultSection?: 'chat' | 'none' | 'participants' | 'plugins' | 'polls';
  enabledSections?: SidebarSection[];
  t?: DyteI18n;
}) {
  const defaultLang = useLanguage();
  t = t ?? defaultLang;
  iconPack = iconPack ?? defaultIconPack;
  const { storeStates, setStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  states = states ?? storeStates;
  const [activeTab, setActiveTab] = useState(states.sidebar ?? defaultSection);
  const selfStageStatus = useDyteSelector((m) => m.stage.status);
  const [enabledSectionsComputed, setEnabledSectionsComputed] =
    useState(enabledSections);

  useEffect(() => {}, [meeting]);
  const closeDialog = () => {
    setStates({ ...states, activeSidebar: false });
  };

  useEffect(() => {
    let newEnabledSections = [...enabledSections];
    if (!canViewChat(meeting)) {
      newEnabledSections = newEnabledSections.filter((e) => e !== 'chat');
    }
    if (!canViewPolls(meeting)) {
      newEnabledSections = newEnabledSections.filter((e) => e !== 'polls');
    }
    if (!canViewParticipants(meeting)) {
      newEnabledSections = newEnabledSections.filter(
        (e) => e !== 'participants'
      );
    }
    if (!canViewPlugins(meeting)) {
      newEnabledSections = newEnabledSections.filter((e) => e !== 'plugins');
    }
    setEnabledSectionsComputed(newEnabledSections);
  }, [selfStageStatus]);

  const styles = StyleSheet.create({
    popUp: {
      borderRadius: 8,
      height: '100%',
      width: '100%',
      position: 'absolute',
      bottom: 0,
      left: 0,
      justifyContent: 'flex-start',
      backgroundColor: colors.background[1000],
      paddingTop: Platform.OS === 'android' ? '5%' : '8%',
    },
    createPollContainer: {
      width: '80%',
      flex: 1,
      flexDirection: 'column',
      backgroundColor: colors.background[800],
      padding: 12,
      margin: 10,
      marginBottom: '8%',
      borderRadius: 20,
      alignItems: 'center',
    },
    button: {
      height: 40,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 16,
      borderBottomWidth: 4,
      borderBottomColor: colors.background[700],
    },
    joinButton: {
      width: '95%',
      height: 50,
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: 4,
      marginBottom: 10,
    },
    closeButton: {
      height: 40,
      alignItems: 'center',
      justifyContent: 'center',
      borderBottomLeftRadius: 2,
      borderTopLeftRadius: 2,
      paddingHorizontal: 16,
      backgroundColor: colors.danger,
    },
    activeTab: {
      borderBottomColor: '#2160FD',
    },
    tabWrapper: {
      flex: 1,
      flexDirection: 'row',
      justifyContent: 'space-evenly',
    },
    tabContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    aboutText: {
      padding: 10,
      marginLeft: '28%',
    },
  });
  return (
    <Modal
      transparent
      visible
      onRequestClose={closeDialog}
      animationType="slide"
    >
      <View style={styles.popUp}>
        {/* eslint-disable-next-line react-native/no-inline-styles */}
        <View style={{ flexDirection: 'row' }}>
          <TouchableHighlight
            underlayColor={colors.background[800]}
            onPress={closeDialog}
            // eslint-disable-next-line react-native/no-inline-styles
            style={{ padding: 10 }}
          >
            <SvgXml
              xml={iconPack.dismiss.replace('currentColor', colors.text)}
              width={20}
              height={20}
            />
          </TouchableHighlight>
          <DyteText style={styles.aboutText}>{t('about_call')}</DyteText>
        </View>
        <View style={styles.tabContainer}>
          <View style={styles.tabWrapper}>
            {enabledSectionsComputed.includes('chat') && (
              <TouchableHighlight
                underlayColor={colors.background[800]}
                onPress={() => setActiveTab('chat')}
              >
                <View
                  style={[
                    styles.button,
                    activeTab === 'chat' ? styles.activeTab : null,
                  ]}
                >
                  <DyteText size="sm">{t('chat')}</DyteText>
                </View>
              </TouchableHighlight>
            )}
            {enabledSectionsComputed.includes('participants') && (
              <TouchableHighlight
                underlayColor={colors.background[800]}
                onPress={() => setActiveTab('participants')}
              >
                <View
                  style={[
                    styles.button,
                    activeTab === 'participants' ? styles.activeTab : null,
                  ]}
                >
                  <DyteText size="sm">{t('participants')}</DyteText>
                </View>
              </TouchableHighlight>
            )}
            {enabledSectionsComputed.includes('polls') && (
              <TouchableHighlight
                underlayColor={colors.background[800]}
                onPress={() => setActiveTab('polls')}
              >
                <View
                  style={[
                    styles.button,
                    activeTab === 'polls' ? styles.activeTab : null,
                  ]}
                >
                  <DyteText size="sm">{t('polls')}</DyteText>
                </View>
              </TouchableHighlight>
            )}
            {enabledSectionsComputed.includes('plugins') && (
              <TouchableHighlight
                underlayColor={colors.background[800]}
                onPress={() => setActiveTab('plugins')}
              >
                <View
                  style={[
                    styles.button,
                    activeTab === 'plugins' ? styles.activeTab : null,
                  ]}
                >
                  <DyteText size="sm">{t('plugins')}</DyteText>
                </View>
              </TouchableHighlight>
            )}
          </View>
        </View>

        {activeTab === 'chat' && <DyteChat meeting={meeting} />}
        {activeTab === 'participants' && <DyteParticipants meeting={meeting} />}
        {activeTab === 'polls' && <DytePolls meeting={meeting} />}
        {activeTab === 'plugins' && <DytePlugins meeting={meeting} />}
      </View>
    </Modal>
  );
}
